import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Search,
  TrendingDown, TrendingUp, Clock, Zap, Filter, Eye,
  RotateCcw, Trash2, Bell, Settings, ChevronRight
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function ErrorMonitorPage() {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState({ total: 0, unresolved: 0, resolved: 0, by_type: {} });
  const [analytics, setAnalytics] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  
  // Filters
  const [filterResolved, setFilterResolved] = useState("all");
  const [filterErrorType, setFilterErrorType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchErrors();
    fetchAnalytics();
    fetchPolicies();
  }, [filterResolved, filterErrorType]);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterResolved !== "all") params.append("resolved", filterResolved === "resolved");
      if (filterErrorType !== "all") params.append("error_type", filterErrorType);
      
      const response = await axios.get(`${API}/workflow-errors?${params.toString()}`, { withCredentials: true });
      setErrors(response.data.errors);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching errors:', error);
      toast.error("Failed to load errors");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/error-analytics?days=30`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/error-policies`, { withCredentials: true });
      setPolicies(response.data.policies);
    } catch (error) {
      console.error('Error fetching policies:', error);
    }
  };

  const handleResolve = async (errorId) => {
    try {
      await axios.post(`${API}/workflow-errors/${errorId}/resolve`, {}, { withCredentials: true });
      toast.success("Error marked as resolved");
      fetchErrors();
    } catch (error) {
      toast.error("Failed to resolve error");
    }
  };

  const handleRetry = async (errorId) => {
    try {
      const response = await axios.post(`${API}/workflow-errors/${errorId}/retry`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success("Workflow retry started");
        fetchErrors();
      } else {
        toast.error(response.data.error || "Retry failed");
      }
    } catch (error) {
      toast.error("Failed to retry workflow");
    }
  };

  const getErrorTypeIcon = (type) => {
    switch (type) {
      case "network": return "🌐";
      case "timeout": return "⏱️";
      case "rate_limit": return "🚦";
      case "auth": return "🔐";
      case "validation": return "📋";
      case "server_error": return "🖥️";
      case "not_found": return "🔍";
      default: return "❓";
    }
  };

  const getErrorTypeBadgeVariant = (type) => {
    switch (type) {
      case "network": return "bg-blue-500/10 text-blue-600 border-blue-500/30";
      case "timeout": return "bg-amber-500/10 text-amber-600 border-amber-500/30";
      case "rate_limit": return "bg-purple-500/10 text-purple-600 border-purple-500/30";
      case "auth": return "bg-red-500/10 text-red-600 border-red-500/30";
      case "validation": return "bg-orange-500/10 text-orange-600 border-orange-500/30";
      case "server_error": return "bg-red-500/10 text-red-600 border-red-500/30";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const filteredErrors = errors.filter(error => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        error.error_message?.toLowerCase().includes(query) ||
        error.node_label?.toLowerCase().includes(query) ||
        error.workflow_id?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="error-monitor-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold">Error Monitor</h1>
            <p className="text-muted-foreground">Track and resolve workflow execution errors</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchErrors}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowPolicyModal(true)}>
              <Settings className="w-4 h-4 mr-2" /> Policies
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Errors</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unresolved</p>
                  <p className="text-2xl font-bold text-red-500">{stats.unresolved}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                  <p className="text-2xl font-bold text-green-500">{stats.resolved}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Retry Success</p>
                  <p className="text-2xl font-bold text-primary">{analytics?.retry_stats?.success_rate || 0}%</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error Type Breakdown */}
        {Object.keys(stats.by_type).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Error Type Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.by_type).map(([type, count]) => (
                  <div 
                    key={type}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => setFilterErrorType(type)}
                  >
                    <span>{getErrorTypeIcon(type)}</span>
                    <span className="capitalize font-medium">{type.replace('_', ' ')}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="errors" className="space-y-4">
          <TabsList>
            <TabsTrigger value="errors">Error Log</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="policies">Policies</TabsTrigger>
          </TabsList>

          {/* Errors Tab */}
          <TabsContent value="errors" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search errors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterResolved} onValueChange={setFilterResolved}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="unresolved">Unresolved</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterErrorType} onValueChange={setFilterErrorType}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Error Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                  <SelectItem value="timeout">Timeout</SelectItem>
                  <SelectItem value="rate_limit">Rate Limit</SelectItem>
                  <SelectItem value="auth">Auth</SelectItem>
                  <SelectItem value="validation">Validation</SelectItem>
                  <SelectItem value="server_error">Server Error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Error List */}
            <Card>
              <ScrollArea className="h-[500px]">
                <div className="divide-y">
                  {loading ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                      Loading errors...
                    </div>
                  ) : filteredErrors.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p className="font-medium">No errors found</p>
                      <p className="text-sm">Your workflows are running smoothly!</p>
                    </div>
                  ) : (
                    filteredErrors.map((error) => (
                      <div 
                        key={error.error_id} 
                        className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => {
                          setSelectedError(error);
                          setShowErrorModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                variant="outline" 
                                className={getErrorTypeBadgeVariant(error.error_type)}
                              >
                                {getErrorTypeIcon(error.error_type)} {error.error_type}
                              </Badge>
                              {error.resolved ? (
                                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Resolved
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">
                                  <XCircle className="w-3 h-3 mr-1" /> Unresolved
                                </Badge>
                              )}
                              {error.retry_count > 0 && (
                                <Badge variant="secondary">
                                  <RotateCcw className="w-3 h-3 mr-1" /> {error.retry_count} retries
                                </Badge>
                              )}
                            </div>
                            <p className="font-medium text-foreground truncate">{error.error_message}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              <span>Node: {error.node_label}</span>
                              <span>•</span>
                              <span>{new Date(error.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!error.resolved && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetry(error.error_id);
                                  }}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResolve(error.error_id);
                                  }}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            {analytics && (
              <>
                {/* Most Error-Prone Workflows */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Most Error-Prone Workflows</CardTitle>
                    <CardDescription>Workflows with the highest error counts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics.error_prone_workflows?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.error_prone_workflows.map((item, idx) => (
                          <div key={item._id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium flex items-center justify-center">
                                {idx + 1}
                              </span>
                              <span className="font-medium">{item.workflow_name}</span>
                            </div>
                            <Badge variant="destructive">{item.error_count} errors</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Common Errors */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Most Common Errors</CardTitle>
                    <CardDescription>Frequently occurring error messages</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics.common_errors?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.common_errors.map((item, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <Badge variant="outline" className={getErrorTypeBadgeVariant(item.error_type)}>
                                {item.error_type}
                              </Badge>
                              <span className="text-sm text-muted-foreground">{item.count} occurrences</span>
                            </div>
                            <p className="text-sm font-mono truncate">{item._id}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Retry Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Retry Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold">{analytics.retry_stats?.total_retried || 0}</p>
                        <p className="text-sm text-muted-foreground">Total Retries</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-500">{analytics.retry_stats?.successful || 0}</p>
                        <p className="text-sm text-muted-foreground">Successful</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-primary">{analytics.retry_stats?.success_rate || 0}%</p>
                        <p className="text-sm text-muted-foreground">Success Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Error Handling Policies</CardTitle>
                    <CardDescription>Define how errors are handled across workflows</CardDescription>
                  </div>
                  <Button size="sm">
                    <Zap className="w-4 h-4 mr-2" /> Create Policy
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {policies.length > 0 ? (
                  <div className="space-y-3">
                    {policies.map((policy) => (
                      <div key={policy.policy_id} className="p-4 rounded-lg border bg-card">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{policy.name}</h4>
                            <p className="text-sm text-muted-foreground">{policy.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">{policy.fallback_action}</Badge>
                              {policy.retry_config?.enabled && (
                                <Badge variant="secondary">
                                  <RotateCcw className="w-3 h-3 mr-1" /> 
                                  {policy.retry_config.maxRetries} retries
                                </Badge>
                              )}
                              {policy.is_global && (
                                <Badge className="bg-primary/10 text-primary">Global</Badge>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No policies configured</p>
                    <p className="text-sm">Create policies to standardize error handling</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Error Detail Modal */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Error Details</DialogTitle>
              <DialogDescription>
                {selectedError?.error_type} error in workflow execution
              </DialogDescription>
            </DialogHeader>
            
            {selectedError && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className={getErrorTypeBadgeVariant(selectedError.error_type)}
                  >
                    {getErrorTypeIcon(selectedError.error_type)} {selectedError.error_type}
                  </Badge>
                  {selectedError.resolved ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                      Resolved
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">
                      Unresolved
                    </Badge>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Error Message</label>
                  <div className="p-3 bg-muted rounded-lg font-mono text-sm">
                    {selectedError.error_message}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Node</label>
                    <p className="font-medium">{selectedError.node_label}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Retry Count</label>
                    <p className="font-medium">{selectedError.retry_count}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Occurred At</label>
                    <p className="font-medium">{new Date(selectedError.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Execution ID</label>
                    <p className="font-mono text-sm">{selectedError.execution_id}</p>
                  </div>
                </div>

                {selectedError.error_stack && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Stack Trace</label>
                    <ScrollArea className="h-32">
                      <pre className="p-3 bg-muted rounded-lg font-mono text-xs whitespace-pre-wrap">
                        {selectedError.error_stack}
                      </pre>
                    </ScrollArea>
                  </div>
                )}

                {selectedError.input_data && Object.keys(selectedError.input_data).length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Input Data</label>
                    <ScrollArea className="h-24">
                      <pre className="p-3 bg-muted rounded-lg font-mono text-xs">
                        {JSON.stringify(selectedError.input_data, null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              {selectedError && !selectedError.resolved && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      handleRetry(selectedError.error_id);
                      setShowErrorModal(false);
                    }}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" /> Retry Workflow
                  </Button>
                  <Button 
                    onClick={() => {
                      handleResolve(selectedError.error_id);
                      setShowErrorModal(false);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Mark Resolved
                  </Button>
                </>
              )}
              {selectedError?.resolved && (
                <Button variant="outline" onClick={() => setShowErrorModal(false)}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
